import { useState, useRef } from "react";
import { Search, X, FileText, Loader2, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import api from "@/lib/api";

export default function SearchDialog({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const doSearch = (value) => {
    if (timer.current) clearTimeout(timer.current);
    if (!value.trim()) {
      setResults([]);
      return;
    }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(value)}`);
        setResults(data.results || []);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const onChange = (e) => {
    const v = e.target.value;
    setQ(v);
    doSearch(v);
  };

  const highlight = (text, term) => {
    if (!term) return text;
    const parts = text.split(new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"));
    return parts.map((p, i) =>
      p.toLowerCase() === term.toLowerCase() ? (
        <mark key={i} className="bg-orange-200 text-orange-900 px-0.5">{p}</mark>
      ) : (
        <span key={i}>{p}</span>
      )
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-sm max-w-2xl p-0 gap-0 overflow-hidden border-zinc-300"
        data-testid="search-dialog"
      >
        <VisuallyHidden asChild>
          <DialogTitle>Search documents</DialogTitle>
        </VisuallyHidden>
        <VisuallyHidden asChild>
          <DialogDescription>
            Type to search text across every uploaded document. Press ESC to close.
          </DialogDescription>
        </VisuallyHidden>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-zinc-200 bg-white">
          <Search className="h-5 w-5 text-zinc-400" />
          <Input
            data-testid="search-input"
            autoFocus
            value={q}
            onChange={onChange}
            placeholder="Search across all uploaded documents..."
            className="border-0 focus-visible:ring-0 shadow-none text-base p-0 h-auto"
          />
          {loading && <Loader2 className="h-4 w-4 animate-spin text-orange-600" />}
          <button
            data-testid="search-close"
            onClick={() => onOpenChange(false)}
            className="p-1 hover:bg-zinc-100 rounded-sm"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto bg-zinc-50">
          {q && !loading && results.length === 0 && (
            <div className="p-10 text-center text-zinc-400 font-mono text-xs">
              No matches for &quot;{q}&quot;
            </div>
          )}
          {!q && (
            <div className="p-10 text-center">
              <FileText className="h-10 w-10 mx-auto text-zinc-300 mb-3" />
              <div className="text-sm text-zinc-500">Type to search text across every uploaded document.</div>
              <div className="text-xs text-zinc-400 mt-2 font-mono">
                Try: "royalty" · "NHAI" · vendor names · dates
              </div>
            </div>
          )}
          {results.map((r) => (
            <Link
              key={r.document_id}
              to={r.project_id ? `/projects/${r.project_id}` : `/documents`}
              onClick={() => onOpenChange(false)}
              data-testid={`search-result-${r.document_id}`}
              className="block px-5 py-4 border-b border-zinc-200 bg-white hover:bg-orange-50 transition-colors"
            >
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4 text-orange-600" />
                <div className="font-medium text-sm flex-1">{r.original_name}</div>
                <span className="font-mono text-[10px] px-1.5 py-0.5 border border-zinc-300 rounded-sm">
                  {r.category}
                </span>
                {r.project_id && <LinkIcon className="h-3 w-3 text-zinc-400" />}
              </div>
              <div className="space-y-1.5">
                {r.matches.slice(0, 3).map((m, i) => (
                  <div key={i} className="text-xs text-zinc-600 font-mono leading-relaxed pl-6">
                    {highlight(m.snippet, q)}
                  </div>
                ))}
                {r.matches.length > 3 && (
                  <div className="text-xs text-zinc-400 pl-6">+ {r.matches.length - 3} more match(es)</div>
                )}
              </div>
            </Link>
          ))}
        </div>

        <div className="px-5 py-2 border-t border-zinc-200 bg-white text-[10px] font-mono text-zinc-400 flex justify-between">
          <span>{results.length} document(s)</span>
          <span>ESC to close</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
